import React from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import CapacityCardsPanel from './CapacityCardsPanel';
import CapacityHistoryPanel from './CapacityHistoryPanel';
import { CapacityCards, CapacityHistoryRow } from './types';
import { computeBudgetPressure, computeCapacity, fetchBieScenarioCombo, fetchCapacityCards, fetchCapacityHistory } from './bieCapacityApi';
import { createFieldObject, type EFieldConfig } from '../../../components/form';
import { formatDate } from '../../../components/renderers';
import { EditablePageField } from '../../../components/egrid';
import BudgetCapacityFilterBar from './BudgetCapacityFilterBar';

type State = { 
  scenarios?: any[];
  scenarioId?:number;
  fiscalYear?: string;
  policyId?:number;
  scenarioCode:string; 
  policyCode:string; 
  lockedCommitments:number; 
  loading:boolean; 
  error:string; 
  cards:CapacityCards|null; 
  history:CapacityHistoryRow[];
  _errors?: any;
};

class BudgetCapacityWorkbench extends React.Component<any, State> {
  
  state: State = { scenarios:[], scenarioCode:'DEMO01', policyCode:'', lockedCommitments:0, loading:false, error:'', cards:null, history:[] };
  
  fieldscfg:{[name:string]:EFieldConfig} = {
      trk: { id: 'trk', label: 'TrackId', type: 'ai', req: 1, width: 200 },
      sci: { id: 'sci', label: 'Scenario', type: 'fa', req: 1, width: 120, cp: {t:'bcs',s:'biescenario'} },
      cpi: { id: 'cpi', label: 'Policy', type: 'fa', req: 0, width: 150, cp: {t:'bcs',s:'biecapacitypolicy'} },
    };
  
  async componentDidMount(){ await this.loadLookups(); await this.load(); }
  
  loadLookups = async () => { this.setState({ loading: true, error: '' }); try { const [scenarios] = await Promise.all([fetchBieScenarioCombo()]); this.setState({ scenarios }); } catch (e: any) { this.setState({ error: e.message || String(e) }); } finally { this.setState({ loading: false }); } };
  
  setTextField = (name:'scenarioCode'|'policyCode'|'fiscalYear') => (e:React.ChangeEvent<HTMLInputElement>) => this.setState({[name]:e.target.value.toUpperCase()} as Pick<State,'scenarioCode'|'policyCode'>);
  
  setLocked = (e:React.ChangeEvent<HTMLInputElement>) => this.setState({lockedCommitments:Number(e.target.value || 0)});
  
  load = async () => { const {scenarioCode}=this.state; this.setState({loading:true,error:''}); try { const [cards,history]=await Promise.all([fetchCapacityCards(scenarioCode),fetchCapacityHistory(scenarioCode)]); this.setState({cards,history}); } catch(err:any){ this.setState({error:err.message || String(err)}); } finally { this.setState({loading:false}); } };
  
  recompute = async () => { 
    const {scenarioId,scenarioCode,policyCode,lockedCommitments}=this.state; 
    this.setState({loading:true,error:''}); 
    try { 
      const cap=await computeCapacity(scenarioCode,policyCode); 
      await computeBudgetPressure(scenarioCode,cap.rid,lockedCommitments); 
      await this.load(); 
    } 
    catch(err:any){ 
      this.setState({error:err.message || String(err)}); 
    } 
    finally { 
      this.setState({loading:false}); 
    } 
  };
  
  makefield = (fieldconf:EFieldConfig):any => {
    const {service = 'bieentry', record = {}} = this.props;
    const mode = 'add';
    const formatters = { dob: formatDate };
    const onChange = this.onChange.bind(this);
    const  extras = {};
    const rtn = createFieldObject(service,mode,fieldconf,record,formatters,onChange, extras);
    return rtn;
  };
  
  onFilterChange = (name: any) => (value:any) => {
    this.setState({[name]: value} as Pick<State, any>);
  };
  
  onChange = (name: any) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    this.setState({[name]: value} as Pick<State, any>);
  };
  
  render(){ 

    const {scenarioId,policyId,_errors,scenarioCode,policyCode,lockedCommitments,loading,error,cards,history}=this.state; 
    const {scenarios,fiscalYear} = this.state;
    const {trk: trkconf, sci: sciconf,cpi: cpiconf} = this.fieldscfg;
    //const trkfield = this.makefield(trkconf);
    const scifield = this.makefield(sciconf);
    const cpifield = this.makefield(cpiconf);
    
    return (
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Budget Capacity Engine</Typography>
            <Typography variant="body2" color="text.secondary">Compare BIE economic capacity against budget consumption and stress level.</Typography>
          </Box>
          <BudgetCapacityFilterBar
            scenarioCode = {scenarioCode} 
            scenarios = {scenarios as any[]} 
            fiscalYear = {fiscalYear || '2026'} 
            onScenarioChange = {this.onFilterChange(scenarioCode)}
            onFiscalYearChange = {this.onFilterChange('fiscalYear')}
            onRefresh = {this.load}
            onCompute = {this.recompute}
            loading = {loading}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={this.load} disabled={loading}>Refresh</Button>
            <Button variant="contained" onClick={this.recompute} disabled={loading}>Recompute</Button>
          </Stack>
        </Stack>
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label="Scenario Code" value={scenarioCode} onChange={this.setTextField('scenarioCode')} fullWidth />
              <EditablePageField 
                  label="Scenario"
                  fieldobject={scifield} 
                  value={scenarioId} 
                  error={_errors?.[scifield.fieldid]} 
                  //readonly={readonly} 
                  onChange={this.onChange('scenarioId')} 
                  //onChange={(value) => onChangeCell(row._rid, col.id, value)} 
                  fullWidth
                />
              </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label="Policy Code" value={policyCode} onChange={this.setTextField('policyCode')} placeholder="Optional" fullWidth />
              <EditablePageField 
                  label="Policy"
                  fieldobject={cpifield} 
                  value={policyId} 
                  error={_errors?.[cpifield.fieldid]} 
                  //readonly={readonly} 
                  onChange={this.onChange('policyId')} 
                  //onChange={(value) => onChangeCell(row._rid, col.id, value)} 
                  fullWidth
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label="Locked Commitments" type="number" value={lockedCommitments} onChange={this.setLocked} fullWidth />
            </Grid>
          </Grid>
        </Paper>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}><CircularProgress size={22} /><Typography>Computing budget capacity...</Typography></Stack>}
        <Stack spacing={3}><CapacityCardsPanel data={cards} /><CapacityHistoryPanel rows={history} /></Stack>
      </Box>
    ); 
  }
}
export default BudgetCapacityWorkbench;
